"use client";
import { Control, UseFormClearErrors } from "react-hook-form";
import TextareaField from "../../../components/common/TextareaField";
import Input from "../../../components/Input";
import NewinputField from "../../../components/NewInput";
import { IconUser } from "@tabler/icons-react";
import { T } from "../../../context/LanguageContext";

type Props = {
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors?: UseFormClearErrors<any>;
  control: Control<any>;
  isEdit?: boolean;
  viewMode?: boolean;
};

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      <T>{title}</T>
    </p>
  </div>
);

const AddCompanyModal = ({
  register,
  errors,
  setValue,
  watch,
  control,
  clearErrors,
  viewMode = false,
}: Props) => {
  // 👇 conditional input style
  const getInputClass = () =>
    `px-4 py-2 text-sm w-full rounded-full outline-none ${
      viewMode
        ? "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
        : "bg-white border border-[#D8D8D8]"
    }`;

  return (
    <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 relative">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
      <SectionHeader icon={IconUser} title="Personal Info" />
      
      <div className="grid grid-cols-2 gap-4">
        <NewinputField
          name="companyName"
          type="text"
          title="Company Name"
          placeholder="Enter Company Name"
          register={register}
          error={errors.companyName}
          disabled={viewMode}
          validation={
            !viewMode
              ? {
                  required: "Company Name is required",
                  pattern: {
                    value: /^(?=.*[A-Za-z])[A-Za-z0-9\s-]+$/,
                    message:
                      "Company Name must include at least one letter (not only numbers)",
                  },
                  validate: (value: string) => {
                    if (!value?.trim())
                      return "Company Name cannot be empty or only spaces";
                    return true;
                  },
                }
              : {}
          }
        />

        <NewinputField
          name="contactPerson"
          type="text"
          title="Contact Person"
          placeholder="Enter Contact Person"
          register={register}
          error={errors.contactPerson}
          disabled={viewMode}
          validation={
            !viewMode
              ? {
                  required: "Contact Person name required",
                  setValueAs: (value: string) => value?.toLowerCase(),
                  pattern: {
                    value: /^(?!\d+$)[A-Za-z0-9\s]+$/,
                    message:
                      "Contact Person must contain letters (not only numbers)",
                  },
                  validate: (value: string) =>
                    value.trim().length > 0 || "Spaces are not allowed",
                }
              : {}
          }
        />

        <NewinputField
          name="mobileNumber"
          type="text"
          title="Mobile Number"
          placeholder="Enter Mobile Number"
          register={register}
          error={errors?.mobileNumber}
          disabled={viewMode}
          validation={
            !viewMode
              ? {
                  required: "Mobile Number is required",
                  validate: (value: string) => {
                    if (!/^\d+$/.test(value)) return "Only digits are allowed";

                    if (value.length < 9 || value.length > 10)
                      return "Mobile Number must be 9 or 10 digits";

                    if (/^0+$/.test(value))
                      return "Mobile Number cannot be all zeros";

                    return true;
                  },
                }
              : {}
          }
          other={{
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              let value = e.target.value.replace(/\D/g, ""); // non-digits strip
              if (value.length > 10) {
                value = value.slice(0, 10);
              }
              setValue("mobileNumber", value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            },
          }}
        />

        <NewinputField
          name="email"
          type="text"
          title="Email"
          placeholder="Enter Email Address"
          register={register}
          error={errors.email}
          disabled={viewMode}
          validation={
            !viewMode
              ? {
                  required: "Email is required",
                  setValueAs: (value: string) => value?.toLowerCase(),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email address",
                  },
                }
              : {}
          }
        />

        <NewinputField
          className="col-span-2"
          name="address"
          type="text"
          title="Address"
          placeholder="Enter Address"
          register={register}
          error={errors.address}
          disabled={viewMode}
          validation={!viewMode ? { required: "Address is required" } : {}}
        />
      </div>
    </div>
  );
};

export default AddCompanyModal;
